import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ChannelCategoryService } from '@service/channel/channel-category.service';
import { ChannelCategory } from 'icreate/common/models/channel-category';
import { ChannelGroup, getExistingRowNums, MAX_CHANNEL_GROUPS } from 'icreate/common/models/channel-group';
import { DialogType } from '../dialog-channel-category/dialog-channel-category.component';
import { Subject, takeUntil } from 'rxjs';
import { Channel, generateNextAvailableNum } from 'icreate/common/models/channel';
import _ from 'lodash';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import { GroupValidator } from '@models/models-validators/group-validator';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { VesselService } from '@service/vessel.service';
import { ValidationError } from 'yup';
import { systemChannelGroupNum } from 'icreate/common/models/system-channels';
import { ChannelGroupService } from '@service/channel/channel-group.service';
import { GenericErrorStrings } from 'icreate/common/validators/validation-errors/generic';

@Component({
    selector: 'app-dialog-channel-group',
    templateUrl: './dialog-channel-group.component.html',
    styleUrls: ['./dialog-channel-group.component.scss'],
    standalone: false,
})
export class DialogChannelGroupComponent implements OnInit, OnDestroy {
    readonly GROUP_ADD = DialogType.add;
    readonly GROUP_EDIT = DialogType.edit;
    readonly GROUP_DELETE = DialogType.delete;
    readonly GROUP_DESCRIPTION = DialogType.description;

    channelGroupList: ChannelGroup[] = [];
    channelGroup: ChannelGroup = new ChannelGroup();
    channelCategoryList: ChannelCategory[] = [];
    channels: { [key: string]: Channel[] } = {};
    groups: ChannelGroup[] = [];

    editDisplayName: string = '';
    selectedGroupId: string;
    selectedCategoryId: string;
    editDisplayNumber: number;
    validationErrors: ValidationErrorMap = {};

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            type: DialogType;
            title: string;
            description: string;
            selectedChannelGroup: ChannelGroup;
            isDuplicate: boolean;
        },
        private channelCategoryService: ChannelCategoryService,
        private channelGroupService: ChannelGroupService,
        private vesselService: VesselService,
        private cdr: ChangeDetectorRef,
        private dialogRef: MatDialogRef<DialogChannelGroupComponent>
    ) {}

    ngOnInit(): void {
        this.channelCategoryService.list$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(channelCategoryList => {
                this.channelCategoryList = channelCategoryList.filter(cat => cat.rowId !== systemChannelGroupNum);
            });

        this.channelGroupService.list$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(channelGroupList => {
            this.channelGroupList = channelGroupList;
        });

        if (this.data.isDuplicate) {
            this.editDisplayName = this.data.selectedChannelGroup.displayName;
            this.selectedCategoryId = this.data.selectedChannelGroup.categoryId;
            const rowNums = getExistingRowNums(this.channelGroupList);
            this.editDisplayNumber = generateNextAvailableNum(rowNums);
            this.getErrors();
        } else if (this.data.selectedChannelGroup?.displayName) {
            this.editDisplayName = this.data.selectedChannelGroup.displayName;
            this.editDisplayNumber = this.data.selectedChannelGroup.rowNum;
            this.selectedCategoryId = this.data.selectedChannelGroup.categoryId;
            this.selectedGroupId = this.data.selectedChannelGroup.id;
        } else {
            const rowNums = getExistingRowNums(this.channelGroupList);
            this.editDisplayNumber = generateNextAvailableNum(rowNums);
        }
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    close() {
        this.dialogRef.close();
    }

    cancelButtonClick() {
        this.dialogRef.close();
    }

    async confirmButtonClick() {
        if (this.data.type === this.GROUP_ADD) {
            await this.addChannelGroup();
        } else if (this.data.type === this.GROUP_EDIT) {
            this.editChannelGroup();
        } else {
            this.deleteChannelGroup();
        }
    }

    saveIsDisabled(): boolean {
        const hasValidationErrors = Object.keys(this.validationErrors)?.length > 0;
        if (this.data.type === this.GROUP_ADD)
            return !this.editDisplayName || !this.selectedCategoryId || !this.editDisplayNumber || hasValidationErrors;
        else if (this.data.type === this.GROUP_EDIT)
            return (
                !this.editDisplayName ||
                !this.selectedCategoryId ||
                !this.editDisplayNumber ||
                hasValidationErrors ||
                (this.editDisplayName === this.data.selectedChannelGroup.displayName &&
                    this.selectedCategoryId === this.data.selectedChannelGroup.categoryId &&
                    this.editDisplayNumber === this.data.selectedChannelGroup.rowNum)
            );
    }

    async addChannelGroup() {
        if (this.saveIsDisabled()) return;
        this.dialogRef.close({
            vesselId: this.vesselService.getActiveVesselId(),
            displayName: this.editDisplayName,
            description: null,
            categoryId: this.selectedCategoryId,
            rowNum: this.editDisplayNumber,
            excelId: null,
        });
    }

    async editChannelGroup() {
        if (this.saveIsDisabled()) return;
        this.dialogRef.close({
            ...this.data.selectedChannelGroup,
            displayName: this.editDisplayName,
            rowNum: this.editDisplayNumber,
            categoryId: this.selectedCategoryId,
        });
    }

    async deleteChannelGroup() {
        this.dialogRef.close(this.data.selectedChannelGroup);
    }

    onInputChange(event: { name: string; value: any }) {
        switch (event.name) {
            case 'displayName':
                this.editDisplayName = event.value;
                break;
            case 'displayNumber':
                const val = event.value?.toString().trim();
                this.editDisplayNumber = val === '' || val == null ? null : !isNaN(val) ? Number(val) : val;
                break;
            case 'categoryId':
                this.selectedCategoryId = event.value;
                break;
        }

        this.getErrors();
        this.cdr.detectChanges();
    }

    getErrors() {
        let validation = {
            displayName: this.editDisplayName,
            displayNumber: this.editDisplayNumber,
            categoryId: this.selectedCategoryId,
        };
        let rowNums = getExistingRowNums(this.channelGroupList);
        if (this.data.type === this.GROUP_EDIT) {
            rowNums = rowNums.filter(row => row !== this.data.selectedChannelGroup.rowNum);
        }
        const errors = getValidationErrors(GroupValidator(rowNums), validation);

        errors.map(error => {
            if (
                error.path == 'displayNumber' &&
                [
                    GenericErrorStrings.ABOVE_MAXIMUM,
                    GenericErrorStrings.BELOW_MINIMUM,
                    GenericErrorStrings.GENERIC_INVALID_VALUE,
                ].includes(error.message)
            ) {
                error.message = 'channel.validation.errors.min-max';
                error.params = {
                    ...error.params,
                    min: 1,
                    max: MAX_CHANNEL_GROUPS,
                };
            } else if (error.path == 'displayNumber' && [GenericErrorStrings.NOT_ONE_OF].includes(error.message)) {
                error.message = 'channel.group.form.number.error.duplicate';
                error.params = {
                    ...error.params,
                };
            }

            return error;
        });

        this.validationErrors = mapErrors(errors);

        if (!this.validationErrors?.displayName) {
            const isDuplicate = this.channelGroupList.some(
                group => group.displayName == this.editDisplayName && group.id !== this.selectedGroupId
            );
            if (isDuplicate) {
                this.validationErrors.displayName = {
                    message: 'channel.group.form.name.error.duplicate',
                } as ValidationError;
            }
        }
    }

    isSystemGroup() {
        if (!this.data?.selectedChannelGroup) return false;
        return this.data.selectedChannelGroup.rowNum == systemChannelGroupNum;
    }
}
